<script setup>
import Icon from '@components/Icon.vue';
import { ref, computed, onMounted } from 'vue';
import moment from 'moment';
import { sortedDays, toRGB } from '@utils';

import { useSettings } from '@store/settings';
const $settings = useSettings();

const props = defineProps({
    short: {
        type: Boolean,
        default: true
    }
});

const useCalendar = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const activeCalender = ref({
        month: 1, // Specify the month (1 for January, 2 for February, etc.)
        year: 0, // Specify the year
        dates: [],
    });

    onMounted(() => {
        activeCalender.value.month = new Date().getMonth() + 1;
        activeCalender.value.year = new Date().getFullYear();
    });

    // generate month dates
    const generateMonthDates = () => {
        const currentDate = moment();

        // Initialize year and month for both modes
        activeCalender.value.year = currentDate.year();
        activeCalender.value.month = currentDate.month() + 1;

        if (props.short) {
            // Short calendar - show next 7 days
            activeCalender.value.dates = [];

            for (let i = 0; i < 7; i++) {
                const date = currentDate.clone().add(i, 'days');
                activeCalender.value.dates.push({
                    day: date.format('dddd'),
                    date: date.date(),
                    month: date.format('MMMM'),
                    year: date.format('YYYY'),
                    fullDate: date.format('YYYY-MM-DD'),
                });
            }
            return;
        }

        // Full calendar - show current month
        const firstDayOfMonth = moment(`${activeCalender.value.year}-${activeCalender.value.month}-01`, 'YYYY-MM-DD');
        const lastDayOfMonth = moment(firstDayOfMonth).endOf('month');

        const startDayOfCalenderWeek = sortedDays()[0];
        const endDayOfCalenderWeek = sortedDays()[sortedDays().length - 1];

        activeCalender.value.dates = [];

        // calculate the number of days to add at the beginning
        const daysToAddAtStart = (firstDayOfMonth.day() + 7 - days.indexOf(startDayOfCalenderWeek)) % 7;

        // add blank objects at the beginning
        let dateStartOfLastMonth = firstDayOfMonth.clone().subtract(daysToAddAtStart, 'days');
        for (let i = 0; i < daysToAddAtStart; i++) {
            activeCalender.value.dates.push({
                day: dateStartOfLastMonth.format('dddd'),
                date: dateStartOfLastMonth.date(),
                month: dateStartOfLastMonth.format('MMMM'),
                year: dateStartOfLastMonth.format('YYYY'),
                fullDate: dateStartOfLastMonth.format('YYYY-MM-DD')
            });

            dateStartOfLastMonth.add(1, 'days');
        }

        // Add all days of the current month
        let currentMonthDate = firstDayOfMonth.clone();
        while (currentMonthDate.isSameOrBefore(lastDayOfMonth)) {
            activeCalender.value.dates.push({
                day: currentMonthDate.format('dddd'),
                date: currentMonthDate.date(),
                month: currentMonthDate.format('MMMM'),
                year: currentMonthDate.format('YYYY'),
                fullDate: currentMonthDate.format('YYYY-MM-DD'),
            });

            currentMonthDate.add(1, 'days');
        }

        // calculate the number of days to add at the end
        const daysToAddAtEnd = ((days.indexOf(endDayOfCalenderWeek) + 7 - currentMonthDate.day()) % 7) + 1;

        // add blank objects at the end
        if (daysToAddAtEnd > 6) return;
        let dateEndOfLastMonth = lastDayOfMonth.clone().add(1, 'days');
        for (let i = 0; i < daysToAddAtEnd; i++) {
            activeCalender.value.dates.push({
                day: dateEndOfLastMonth.format('dddd'),
                date: dateEndOfLastMonth.date(),
                month: dateEndOfLastMonth.format('MMMM'),
                year: dateEndOfLastMonth.format('YYYY'),
                fullDate: dateEndOfLastMonth.format('YYYY-MM-DD')
            });

            dateEndOfLastMonth.add(1, 'days');
        }
    };

    /**
     * Divide an array into groups of size
     * 
     * @param items - Array to be divided
     * @param groupSize - Size of each group
     * @returns - Array of groups
     * Example: divideArrayIntoGroups([1, 2, 3, 4, 5, 6, 7], 3) => [[1, 2, 3], [4, 5, 6], [7]]
     */
    const divideArrayIntoGroups = (items, groupSize) => {
        const resultArray = [];
        for (let i = 0; i < items.length; i += groupSize) {
            resultArray.push(items.slice(i, i + groupSize));
        }
        return resultArray;
    };

    // get dates for calender
    const getDates = computed(() => {
        generateMonthDates();
        return divideArrayIntoGroups(activeCalender.value.dates, 7);
    });

    // check if today
    const isToday = (fullDate) => {
        return moment().format('YYYY-MM-DD') === fullDate;
    };

    // check if current month
    const isDisabled = (fullDate) => {
        // if not current month
        if (moment(`${activeCalender.value.year}-${activeCalender.value.month}`, 'YYYY-M').format('YYYY-MM') !== moment(fullDate, 'YYYY-MM-DD').format('YYYY-MM')) return true;

        // if date is past from today
        if (moment(fullDate, 'YYYY-MM-DD').isBefore(moment().format('YYYY-MM-DD'))) return true;

        // if date is off
        const dayName = moment(fullDate, 'YYYY-MM-DD').format('dddd'); // Gets day name like "Monday"
        if (!$settings.businessHours[dayName].enabled) return true;

        // if date is holiday
        return $settings.daysOff.holidays.some(holidayGroup => {
            if (holidayGroup.length === 1) {
                // Single date holiday
                return moment(holidayGroup[0].date, 'YYYY-MM-DD').isSame(moment(fullDate, 'YYYY-MM-DD'));
            } else if (holidayGroup.length === 2) {
                // Date range holiday
                const startDate = moment(holidayGroup[0].date, 'YYYY-MM-DD');
                const endDate = moment(holidayGroup[1].date, 'YYYY-MM-DD');
                const checkDate = moment(fullDate, 'YYYY-MM-DD');
                return checkDate.isBetween(startDate, endDate, 'day', '[]'); // [] means inclusive
            }
            return false;
        });
    };

    const changeMonth = (option) => {
        if (!option) return;

        // on prev if month is current month then return
        if (option == 'prev' && moment().month(activeCalender.value.month - 1).format('MMMM') === moment().format('MMMM')) return;
        console.log(option);
        // on next if month is future then return
        if (option == 'next' && moment().month(activeCalender.value.month - 1).format('MMMM') === moment().add(4, 'month').format('MMMM')) return;
        const currentMonth = moment({
            year: activeCalender.value.year,
            month: activeCalender.value.month - 1, // Moment.js months are 0-based
        });

        // get the details of next or prev month
        const changeMonthTo = option == 'next' ? currentMonth.clone().add(1, 'month') : currentMonth.clone().subtract(1, 'month');

        activeCalender.value.month = changeMonthTo.month() + 1;
        activeCalender.value.year = changeMonthTo.year();
        activeCalender.value.dates = [];

        // getDates.value
        generateMonthDates();

    };


    return { getDates, isToday, isDisabled, activeCalender, changeMonth };
};
const { getDates, isToday, isDisabled, activeCalender, changeMonth } = useCalendar();

const progressHeight = () => {
    // bail, if progress is not enabled
    if (!$settings.customizer.date.show_progress) return;
    else return Math.round(Math.random() * 60);
};

const expandCalendar = () => {
    props.short = !props.short;
}

</script>

<template>
<div>
    <div class="flex items-center justify-between gap-2">
        <div class="text-[#344054] text-base font-semibold">{{ moment(`${activeCalender.year}-${activeCalender.month}`, 'YYYY-M').format('MMMM YYYY') }}</div>
        <Icon :name="props.short ? 'expand01' : 'minimize01'" @click="expandCalendar()" size="20px" class="text-[#667085] cursor-pointer transition-all" />
    </div>
    <hr class="my-2 border-[#EAECF0]">

    <div class="mt-2">
        <div class="flex justify-between">
            <div v-for="day in sortedDays()" :key="day" class="flex justify-center items-center w-10 h-10 text-sm leading-5 font-medium text-[#667085]">{{ day.slice(0, 3) }}</div>
        </div>

        <!-- calendar dates -->
        <div v-for="(row, index) in getDates" :key="index" class="flex justify-between gap-1">
            <div v-for="item in row" :key="item" class="overflow-hidden relative border text-base leading-6 font-semibold text-center w-10 h-10 mb-[28.6px] border-[#dbcaef] rounded cursor-pointer" :class="{ '!text-white !border-2 !border-solid': isToday(item.fullDate) }, { '!text-[#98a2b3] !cursor-not-allowed !border-transparent': isDisabled(item.fullDate) }" :style="`background-color: ${isToday(item.fullDate) ? `#101828 !important` : 'transparent'}; border: 1px solid ${isToday(item.fullDate) ? `#101828` : `#EAECF0`};`">
                <div v-if="!isToday(item.fullDate) && !isDisabled(item.fullDate)" class="absolute bottom-0 left-0 w-full transition-all" :style="`height:${progressHeight()}%; background-color: ${isToday(item.fullDate) ? `#101828` : `#EAECF0`} !important;`"></div>
                <div class="flex absolute justify-center items-center w-full h-full">{{ item.date }}</div>
            </div>
        </div>
    </div>
</div>
</template>


<style></style>